import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getLocations, createLocation, deleteLocation, updateLocation } from "../api";
import { useNavigate, useLocation } from "react-router-dom";
import { useNotification } from "../../../contexts/NotificationContext";

export const useGetLocations = () => {
  return useQuery({
    queryKey: ["locations"],
    queryFn: () => getLocations(),
  });
};

export const useCreateLocation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();
  
  return useMutation({
    mutationFn: (data) => createLocation(data),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["locations"] });
      
      showNotification('success', 'Location created successfully!');
      
      const searchParams = new URLSearchParams(location.search);
      const from = searchParams.get('from');
      const vin = searchParams.get('vin');

      if (from === 'add-inventory' && vin) {
        navigate(`/add-inventory?vin=${encodeURIComponent(vin)}`);
      } else {
        navigate('/inventory-parameters?tab=locations');
      }
    },
    onError: (error) => {
      console.error("Error creating location:", error);
      
      // Show error notification
      let errorMessage = 'Error creating location. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
};

export const useDeleteLocation = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  
  return useMutation({
    mutationFn: (id) => deleteLocation(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["locations"] });
      
      // Show success notification
      showNotification('success', 'Location deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting location:", error);
      
      // Show error notification
      let errorMessage = 'Error deleting location. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
};

export const useUpdateLocation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  return useMutation({
    mutationFn: ({ id, data }) => updateLocation(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["locations"] });
      
      // Show success notification
      showNotification('success', 'Location updated successfully!');
      
      // Navigate immediately without setTimeout
      navigate('/inventory-parameters?tab=locations');
    },
    onError: (error) => {
      console.error("Error updating location:", error);
      
      // Show error notification
      let errorMessage = 'Error updating location. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      showNotification('danger', errorMessage);
    },
  });
}; 